import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';

import useQuestionnaire from 'hooks/useQuestionnaire';
import { checkSpanishLocale, cn } from 'lib/utils';
import { useQuestionnaireStore } from 'store/questionnaire';
import QuestionnaireContainer from './components/QuestionnaireContainer';

import LessThanOneLitre from 'assets/images/questionnaire/one-litre.png';
import OneToTwoLitre from 'assets/images/questionnaire/one-two-litre.png';
import MoreThanTwoLitre from 'assets/images/questionnaire/more-two-litre.png';

export default function WaterConsumption() {
  const { t } = useTranslation();
  const questionData = useQuestionnaireStore((store) => store.questionData);

  const isSpanish = checkSpanishLocale();

  const { onSubmit, isLoading } = useQuestionnaire();

  const [selectedIndex, setSelectedIndex] = useState(0);

  const englishChoices = JSON.parse(questionData?.eng_choices || '{}');
  const spanishChoices = JSON.parse(questionData?.spanish_choices || '{}');

  const consumptions = useMemo(
    () =>
      [
        {
          image: LessThanOneLitre,
          label: t('water_consumption_option_title_1'),
          description: t('water_consumption_option_desc_1'),
        },
        {
          image: OneToTwoLitre,
          label: t('water_consumption_option_title_2'),
          description: t('water_consumption_option_desc_2'),
        },
        {
          image: MoreThanTwoLitre,
          label: t('water_consumption_option_title_3'),
          description: t('water_consumption_option_desc_3'),
        },
      ].map((consumption, index) => ({
        ...consumption,
        en: englishChoices[index],
        es: spanishChoices[index],
      })),
    [englishChoices, spanishChoices]
  );

  useEffect(() => {
    if (questionData?.user_eng_choices || questionData?.user_spanish_choices) {
      const labels = consumptions.map(
        (langLevel) => langLevel?.[isSpanish ? 'es' : 'en']
      );
      const userChoices =
        questionData?.[isSpanish ? 'user_spanish_choices' : 'user_eng_choices'];
      let answer = '';
      try {
        const parsedChoices = JSON.parse(userChoices);
        answer = parsedChoices[0];
      } catch (error) {
        console.log(error);
      }
      const index = labels.indexOf(answer);
      setSelectedIndex(index);
    } else {
      const index = 1;
      setSelectedIndex(index);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questionData]);

  const onContinue = async () => {
    onSubmit({
      choice_value: JSON.stringify([consumptions?.[selectedIndex]?.en]),
      spanish_choice_value: JSON.stringify([consumptions?.[selectedIndex]?.es]),
      questionData,
    });
  };

  return (
    <QuestionnaireContainer
      questionData={questionData}
      isLoading={isLoading}
      onContinue={onContinue}
      questionNumber={7}
    >
      <div className='relative grid flex-wrap items-center grid-cols-1 gap-4 md:grid-cols-3 lg:gap-10 xl:gap-20 lg:flex'>
        {consumptions?.map((consumption, index) => {
          const isSelected = selectedIndex === index;

          return (
            <div
              key={consumption.en.label}
              className='relative grid cursor-pointer place-items-center'
              onClick={() => setSelectedIndex(index)}
            >
              {isSelected && (
                <motion.div
                  layoutId='body-type-indicator'
                  className='absolute z-0 size-28 md:size-32 lg:size-36 top-0 rounded-[24px] border-[2px] border-primary bg-primary/10'
                  transition={{
                    type: 'spring',
                    stiffness: 350,
                    damping: 40,
                  }}
                />
              )}

              <div
                className={cn(
                  'relative z-10 grid border-[2px] place-items-center size-28 md:size-32 lg:size-36 rounded-[24px]',
                  isSelected && 'border-transparent'
                )}
              >
                <img
                  src={consumption.image}
                  alt={consumption.label}
                  className='object-contain w-full h-16 md:h-20 lg:h-24'
                />
              </div>

              <p
                className={cn(
                  'font-medium capitalize text-center mt-4 text-sm z-10 w-40 leading-5 transition-all duration-150',
                  isSelected ? 'text-primary' : 'text-gray-400'
                )}
              >
                {consumption?.[isSpanish ? 'es' : 'en']} [
                {consumption.description}]
              </p>
            </div>
          );
        })}
      </div>
    </QuestionnaireContainer>
  );
}
